// 🗂️ Procesar Recibidos / Procesar Emitidos — Financiero / Gestión SRI
// Componente genérico: clasifica comprobantes del SRI asignando Categoría y
// Partner (global y por línea). Recibidos graba en tickets_buy/ticketlines_buy/
// taxlines_buy/payments_buy; emitidos en tickets/ticketlines/taxlines/payments.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  FileSpreadsheet,
  FileText,
  Filter,
  Loader2,
  Save,
  type LucideIcon,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import { labelCentroCosto, useCentrosContables, useCentroCostoModo } from '../financiero/centroCosto';
import { PanelIntegrado, useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { DetallePrefactura } from '../financiero/PrefacturasProveedor';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const usd = (v: number | string | null) =>
  v == null ? '—' : Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });

// Los archivos viven en /opt/sri/documentos/{recibidos,emitidos} y Apache los
// sirve en /sri-docs (symlink en htdocs). URL relativa: misma raíz que la app.
const urlRide = (rideUrl: string | null) =>
  rideUrl ? rideUrl.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null;

interface ModoConfig {
  titulo: string;
  descripcion: string;
  icono: LucideIcon;
  contraparte: string;          // etiqueta de la columna: Emisor / Receptor
  campoRuc: string;             // ruc_emisor / ruc_receptor
  campoRazon: string;           // razon_social_emisor / razon_social_receptor
  tipos: string[];
  rpcListar: string;
  rpcDetalle: string;
  rpcGrabar: string;
  rpcGrabarMasivo: string;
  rpcCategorias: string;
  soportaAplica?: boolean;      // muestra el filtro "Aplica a (documento)" (num_doc_modificado)
}

// El proyecto (centro de costo) es opcional; su catálogo es financiero.partner.
const PROYECTO_VACIO = '— Sin proyecto —';

// NC / ND / Retención heredan su clasificación del documento de sustento: al
// procesar (individual o masivo) no requieren categoría/proyecto/centro.
const heredaTipo = (tipo?: string | null) =>
  /retenci|nota\s*de\s*cr[eé]dito|nota\s*de\s*d[eé]bito/i.test(tipo || '');

const MODO_RECIBIDOS: ModoConfig = {
  titulo: 'Procesar Recibidos',
  descripcion: 'Clasifica los comprobantes recibidos por categoría y partner',
  icono: ClipboardCheck,
  contraparte: 'Emisor',
  campoRuc: 'ruc_emisor',
  campoRazon: 'razon_social_emisor',
  tipos: ['Factura', 'Liquidación de compra', 'Nota de Crédito', 'Nota de Débito', 'Comprobante de Retención'],
  rpcListar: 'sri_procesar_listar',
  rpcDetalle: 'sri_procesar_detalle',
  rpcGrabar: 'sri_procesar_grabar',
  rpcGrabarMasivo: 'sri_procesar_grabar_masivo',
  rpcCategorias: 'sri_categorias',
  soportaAplica: true,
};

const MODO_EMITIDOS: ModoConfig = {
  titulo: 'Procesar Emitidos',
  descripcion: 'Clasifica los comprobantes emitidos (ventas) por categoría y partner',
  icono: ClipboardList,
  contraparte: 'Receptor',
  campoRuc: 'ruc_receptor',
  campoRazon: 'razon_social_receptor',
  tipos: ['Factura', 'Liquidación de compra', 'Nota de Crédito', 'Nota de Débito', 'Guía de Remisión', 'Comprobante de Retención'],
  rpcListar: 'sri_procesar_emitidos_listar',
  rpcDetalle: 'sri_procesar_emitidos_detalle',
  rpcGrabar: 'sri_procesar_emitidos_grabar',
  rpcGrabarMasivo: 'sri_procesar_emitidos_grabar_masivo',
  rpcCategorias: 'sri_categorias_emitidos',
};

export function ProcesarRecibidos() {
  return <ProcesarDocumentos cfg={MODO_RECIBIDOS} />;
}

export function ProcesarEmitidos() {
  return <ProcesarDocumentos cfg={MODO_EMITIDOS} />;
}

interface DocumentoLista {
  id: string;
  fecha_emision: string | null;
  tipo_comprobante: string;
  serie_comprobante: string | null;
  importe_total: number | null;
  ride_url: string | null;
  xml_parseado: boolean;
  parse_error: string | null;
  procesado: boolean;
  categoria: string | null;
  // Tercer estado (script 829): revisada y descartada, con motivo. Sin él,
  // «la revisé y no corresponde» y «nadie la ha mirado» son la misma fila.
  descartado?: boolean;
  descarte_motivo?: string | null;
  descartado_por?: string | null;
  origen_erp?: boolean;         // emitido que salió del propio ERP (solo emitidos)
  [campo: string]: unknown;     // ruc_emisor/razon_social_emisor o *_receptor
}

interface Partner {
  id: string;
  razon_social: string;
  identificacion: string;
  tipo_partner: string[];
}

interface LineaDetalle {
  numero_linea: number;
  codigo_principal: string | null;
  descripcion: string;
  cantidad: number | null;
  precio_unitario: number | null;
  descuento: number | null;
  precio_total_sin_impuesto: number | null;
  iva_tarifa: number | null;
  iva_valor: number | null;
  categoria: string | null;
  proyecto_id: string | null;
}

interface Detalle {
  documento: ({
    id: string;
    tipo_comprobante: string;
    serie_comprobante: string | null;
    clave_acceso: string;
    fecha_emision: string | null;
    valor_sin_impuestos: number | null;
    iva: number | null;
    importe_total: number | null;
    xml_parseado: boolean;
    parse_error: string | null;
  } & Record<string, unknown>) | null;
  ticket: {
    id: string; origen: string | null; categoria: string | null; proyecto_id: string | null;
    proyecto_nombre: string | null; centro_costo_id: string | null; centro_costo_nombre: string | null;
    cc_contable_id?: string | null;
    partner_nombre: string | null; observaciones: string | null;
  } | null;
  // NC/ND/Retención: clasificación heredada del documento de sustento. NULL si el
  // comprobante no hereda (factura, liquidación, etc.).
  sustento: {
    tipo: 'nc_nd' | 'retencion';
    num_doc: string | null;           // documento modificado / sustentado
    encontrado: boolean;              // el documento de sustento existe en el sistema
    procesado: boolean;               // el documento de sustento ya fue clasificado
    categoria: string | null;
    proyecto_id: string | null;
    proyecto_nombre: string | null;
    centro_costo_id: string | null;
    centro_costo_nombre: string | null;
    observaciones: string | null;
  } | null;
  erp_origen: {
    ticket_id: string; origen: string; categoria: string | null;
    proyecto_id: string | null; proyecto_nombre: string | null;
    centro_costo_id: string | null; centro_costo_nombre: string | null;
    cc_contable_id?: string | null;
  } | null;
  lineas: LineaDetalle[];
  impuestos: Array<{ id: number; codigo: string; codigo_porcentaje: string; tarifa: number | null; base_imponible: number; valor: number }>;
  pagos: Array<{ id: number; forma_pago: string; descripcion: string; total: number; plazo: number | null; unidad_tiempo: string | null }>;
}

type AsignacionLinea = { categoria: string; proyecto_id: string };

const selectCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';
const inputCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

type CampoOrden = 'fecha' | 'tipo' | 'contraparte' | 'total' | 'estado';

// Cabecera de columna ordenable (clic alterna asc/desc)
function ThOrden({
  campo, actual, onSort, alinear = 'left', children,
}: {
  campo: CampoOrden;
  actual: { campo: CampoOrden; dir: 'asc' | 'desc' };
  onSort: (c: CampoOrden) => void;
  alinear?: 'left' | 'right';
  children: React.ReactNode;
}) {
  const activo = actual.campo === campo;
  const Icono = !activo ? ArrowUpDown : actual.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`py-2 pr-3 ${alinear === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onSort(campo)}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${
          alinear === 'right' ? 'flex-row-reverse' : ''
        } ${activo ? 'text-foreground font-semibold' : ''}`}
        title="Ordenar"
      >
        {children}
        <Icono className={`w-3 h-3 ${activo ? '' : 'opacity-40'}`} />
      </button>
    </th>
  );
}

function ProcesarDocumentos({ cfg }: { cfg: ModoConfig }) {
  const queryClient = useQueryClient();
  const anioActual = new Date().getFullYear();
  const Icono = cfg.icono;

  const [filtros, setFiltros] = useState({
    anio: String(anioActual), mes: '', tipo: '', ruc: '', razonSocial: '', serie: '', aplica: '', estado: 'pendientes',
  });
  const [aplicados, setAplicados] = useState(filtros);

  const { data: categorias = [] } = useQuery<string[]>({
    queryKey: ['sriCategorias', cfg.rpcCategorias],
    queryFn: async () => (await apiClient.post(`/rpc/${cfg.rpcCategorias}`, {}, FIN)).data || [],
    staleTime: Infinity,
  });

  const { data: partners = [] } = useQuery<Partner[]>({
    queryKey: ['sriPartners'],
    queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
    staleTime: 60_000,
  });

  const { data: documentos = [], isFetching } = useQuery<DocumentoLista[]>({
    queryKey: ['sriProcesarListar', cfg.rpcListar, aplicados],
    queryFn: async () => {
      const { data } = await apiClient.post(`/rpc/${cfg.rpcListar}`, {
        _anio: aplicados.anio ? Number(aplicados.anio) : null,
        _mes: aplicados.mes ? Number(aplicados.mes) : null,
        _tipo: aplicados.tipo || null,
        _ruc: aplicados.ruc || null,
        _razon_social: aplicados.razonSocial || null,
        _estado: aplicados.estado,
        _serie: aplicados.serie || null,
        _aplica: aplicados.aplica || null,
      }, FIN);
      return data || [];
    },
  });

  const [docId, setDocId] = useState<string | null>(null);

  // --- ordenamiento por columna ---
  const [orden, setOrden] = useState<{ campo: CampoOrden; dir: 'asc' | 'desc' }>({ campo: 'fecha', dir: 'desc' });

  const ordenarPor = (campo: CampoOrden) =>
    setOrden((prev) => prev.campo === campo
      ? { campo, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { campo, dir: 'asc' });

  const valorOrden = (d: DocumentoLista, campo: CampoOrden): string | number => {
    switch (campo) {
      case 'fecha': return d.fecha_emision || '';
      case 'tipo': return `${d.tipo_comprobante} ${d.serie_comprobante || ''}`.toLowerCase();
      case 'contraparte': return String(d[cfg.campoRazon] || '').toLowerCase();
      case 'total': return Number(d.importe_total) || 0;
      case 'estado': return !d.xml_parseado ? 0 : d.procesado ? 2 : 1; // sin detalle < pendiente < procesado
    }
  };

  const documentosOrdenados = useMemo(() => {
    const factor = orden.dir === 'asc' ? 1 : -1;
    return [...documentos].sort((a, b) => {
      const va = valorOrden(a, orden.campo);
      const vb = valorOrden(b, orden.campo);
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
  }, [documentos, orden]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- modo de centro de costo (partner vs contable), por empresa ---
  const { modo: modoCentro } = useCentroCostoModo();
  const { data: centrosContables = [] } = useCentrosContables(modoCentro === 'contable');
  const labelCentro = labelCentroCosto(modoCentro);

  // --- asignación masiva ---
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());

  // Clasificación aprendida del proveedor (script 769). Sólo aplica a RECIBIDOS:
  // el historial es "cómo se clasificó a ESTE proveedor antes", y eso sólo
  // existe del lado de las compras.
  const aprendeDelProveedor = cfg.rpcGrabarMasivo === 'sri_procesar_grabar_masivo';
  const { data: sugerencias = [] } = useQuery<any[]>({
    enabled: aprendeDelProveedor,
    queryKey: ['sri-sugerencias', aplicados.anio, aplicados.mes],
    queryFn: async () => (await apiClient.post('/rpc/fin_clasificacion_sugerida', { _documento_ids: null, _limite: 500 }, FIN)).data || [],
  });
  const sugPorDoc = useMemo(() => new Map(sugerencias.map((s) => [s.documento_id, s])), [sugerencias]);
  const conSugerenciaAlta = useMemo(
    () => [...seleccion].filter((id) => sugPorDoc.get(id)?.sugerencia?.dimensiones?.categoria?.nivel === 'alta').length,
    [seleccion, sugPorDoc]);

  const aplicarSugerencias = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/fin_clasificacion_aplicar',
      { _documento_ids: [...seleccion], _nivel_min: 'alta' }, FIN)).data,
    onSuccess: (r: any) => {
      // Se informa lo aplicado Y lo omitido con su motivo: un "listo" a secas
      // sobre 200 documentos no dice si quedaron 40 fuera.
      const partes = [`${r.clasificados} clasificado(s)`];
      if (r.omitidos > 0) partes.push(`${r.omitidos} sin sugerencia fiable`);
      if (r.con_error > 0) partes.push(`${r.con_error} con error`);
      (r.omitidos > 0 || r.con_error > 0 ? toast.warning : toast.success)(partes.join(' · '));
      setSeleccion(new Set());
      void queryClient.invalidateQueries({ queryKey: ['sriProcesarListar'] });
      void queryClient.invalidateQueries({ queryKey: ['sri-sugerencias'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudieron aplicar las sugerencias'),
  });
  // Descartar es el tercer estado que faltaba: sin él la bandeja no baja nunca
  // aunque se trabaje, porque «revisada y no corresponde» seguía contando como
  // pendiente para siempre.
  const descartar = useMutation({
    mutationFn: async (motivo: string) =>
      (await apiClient.post('/rpc/fin_recibido_descartar',
        { _ids: [...seleccion], _motivo: motivo }, FIN)).data,
    onSuccess: (r: any) => {
      toast.success(r?.mensaje || 'Descartados');
      setSeleccion(new Set());
      void queryClient.invalidateQueries({ queryKey: ['sriProcesarListar'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo descartar'),
  });

  const [masivo, setMasivo] = useState({ categoria: '', proyectoId: '', centroId: '' });
  const centros = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('centro_costo')), [partners]);
  const elegibles = documentos.filter((d) => d.xml_parseado);
  const todosSeleccionados = elegibles.length > 0 && elegibles.every((d) => seleccion.has(d.id));

  // Composición de la selección: cuántos heredan del sustento vs cuántos requieren clasificación.
  const selDocs = useMemo(() => documentos.filter((d) => seleccion.has(d.id)), [documentos, seleccion]);
  const heredanN = selDocs.filter((d) => heredaTipo(d.tipo_comprobante)).length;
  const requierenN = selDocs.length - heredanN;

  const toggleSeleccion = (id: string) =>
    setSeleccion((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });

  const toggleTodos = () =>
    setSeleccion(todosSeleccionados ? new Set() : new Set(elegibles.map((d) => d.id)));

  // --- exportar la tabla (CSV / XLS / PDF) — respeta filtros y orden actuales ---
  const nombreArchivo = `${cfg.titulo.toLowerCase().replace(/ /g, '-')}-${fechaLocal()}`;
  const HEADERS_EXP = ['N°', 'Fecha', 'Tipo', 'Serie', cfg.contraparte, 'Identificación', 'Total', 'Estado', 'Categoría'];
  const estadoTxt = (d: DocumentoLista) => (d.procesado ? 'Procesado' : d.descartado ? 'Descartado' : d.xml_parseado ? 'Pendiente' : 'Sin detalle');
  const totalExp = documentosOrdenados.reduce((a, d) => a + Number(d.importe_total || 0), 0);
  const filasExport: Celda[][] = [
    ...documentosOrdenados.map((d, i): Celda[] => [
      i + 1, d.fecha_emision ?? '', d.tipo_comprobante ?? '', d.serie_comprobante ?? '',
      String(d[cfg.campoRazon] ?? ''), String(d[cfg.campoRuc] ?? ''),
      Number(d.importe_total ?? 0), estadoTxt(d), d.categoria ?? '',
    ]),
    ['', '', '', '', '', 'TOTAL', totalExp, '', ''],
  ];

  const grabarMasivo = useMutation({
    mutationFn: async () => {
      const { data: res } = await apiClient.post(`/rpc/${cfg.rpcGrabarMasivo}`, {
        _documento_ids: [...seleccion],
        _categoria: masivo.categoria || null,
        _proyecto_id: masivo.proyectoId || null,
        _centro_costo_id: modoCentro === 'contable' ? null : (masivo.centroId || null),
        _cc_contable_id: modoCentro === 'contable' ? (masivo.centroId || null) : null,
      }, FIN);
      return res as { procesados: number; errores: Array<{ documento_id: string; error: string }> };
    },
    onSuccess: (res) => {
      if (res.errores?.length) {
        toast.warning(`${res.procesados} procesados · ${res.errores.length} con error (${res.errores[0].error})`);
      } else {
        toast.success(`${res.procesados} documentos procesados`);
      }
      setSeleccion(new Set());
      void queryClient.invalidateQueries({ queryKey: ['sriProcesarListar'] });
      void queryClient.invalidateQueries({ queryKey: ['sriPartners'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo procesar el lote'),
  });

  const dlg = useDialogoEnLinea();

  return (
    <div className="p-6 space-y-6">
      {dlg.ui}
      <div className="flex items-center gap-3">
        <Icono className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">{cfg.titulo}</h1>
          <p className="text-sm text-muted-foreground">{cfg.descripcion}</p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="pr-anio">Año</label>
              <select id="pr-anio" className={selectCls} value={filtros.anio}
                onChange={(e) => setFiltros({ ...filtros, anio: e.target.value })}>
                <option value="">Todos</option>
                {Array.from({ length: anioActual - 2014 }, (_, i) => anioActual - i).map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="pr-mes">Mes</label>
              <select id="pr-mes" className={selectCls} value={filtros.mes}
                onChange={(e) => setFiltros({ ...filtros, mes: e.target.value })}>
                <option value="">Todos</option>
                {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="pr-tipo">Tipo de comprobante</label>
              <select id="pr-tipo" className={selectCls} value={filtros.tipo}
                onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}>
                <option value="">Todos</option>
                {cfg.tipos.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="pr-ruc">RUC {cfg.contraparte.toLowerCase()}</label>
              <input id="pr-ruc" className={inputCls} placeholder="099…" value={filtros.ruc}
                onChange={(e) => setFiltros({ ...filtros, ruc: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="pr-razon">Razón social {cfg.contraparte.toLowerCase()}</label>
              <input id="pr-razon" className={inputCls} placeholder="Contiene…" value={filtros.razonSocial}
                onChange={(e) => setFiltros({ ...filtros, razonSocial: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="pr-serie">Serie (documento)</label>
              <input id="pr-serie" className={inputCls} placeholder="001-001-000…" value={filtros.serie}
                onChange={(e) => setFiltros({ ...filtros, serie: e.target.value })} />
            </div>
            {cfg.soportaAplica && (
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="pr-aplica">Aplica a (documento)</label>
                <input id="pr-aplica" className={inputCls} placeholder="Nº doc. modificado…" value={filtros.aplica}
                  onChange={(e) => setFiltros({ ...filtros, aplica: e.target.value })} />
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="pr-estado">Estado</label>
              <select id="pr-estado" className={selectCls} value={filtros.estado}
                onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}>
                <option value="todos">Todos</option>
                <option value="pendientes">Pendientes</option>
                <option value="procesados">Procesados</option>
                <option value="descartados">Descartados</option>
              </select>
            </div>
            <Button size="sm" onClick={() => { setAplicados(filtros); setSeleccion(new Set()); }} isLoading={isFetching}>
              {!isFetching && <Filter className="w-4 h-4" />}
              Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Asignación masiva */}
      {seleccion.size > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <p className="text-sm font-semibold self-center">
                {seleccion.size} seleccionado{seleccion.size === 1 ? '' : 's'}
              </p>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ms-cat">Categoría {requierenN > 0 ? '*' : '(opcional)'}</label>
                <select id="ms-cat" className={selectCls} value={masivo.categoria}
                  onChange={(e) => setMasivo({ ...masivo, categoria: e.target.value })}>
                  <option value="">— Seleccione —</option>
                  {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ms-proyecto">Proyecto</label>
                <select id="ms-proyecto" className={`${selectCls} max-w-72`} value={masivo.proyectoId}
                  onChange={(e) => setMasivo({ ...masivo, proyectoId: e.target.value })}>
                  <option value="">{PROYECTO_VACIO}</option>
                  {partners.map((p) => (
                    <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ms-centro">{labelCentro} {requierenN > 0 ? '*' : '(opcional)'}</label>
                <select id="ms-centro" className={`${selectCls} max-w-72`} value={masivo.centroId}
                  onChange={(e) => setMasivo({ ...masivo, centroId: e.target.value })}>
                  <option value="">— Seleccione —</option>
                  {modoCentro === 'contable'
                    ? centrosContables.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)
                    : centros.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
                </select>
              </div>
              <Button size="sm" isLoading={grabarMasivo.isPending} onClick={() => {
                if (requierenN > 0 && !masivo.categoria) { toast.error('Seleccione la categoría (hay documentos que la requieren)'); return; }
                if (requierenN > 0 && !masivo.centroId) { toast.error('Seleccione el centro de costo (hay documentos que lo requieren)'); return; }
                grabarMasivo.mutate();
              }}>
                {!grabarMasivo.isPending && <CheckCircle2 className="w-4 h-4" />}
                Asignar a {seleccion.size}
              </Button>
              {aprendeDelProveedor && conSugerenciaAlta > 0 && (
                <Button size="sm" variant="outline" isLoading={aplicarSugerencias.isPending}
                  title="Usa la clasificación con la que se procesó antes a cada proveedor. Sólo aplica las de confianza alta; el resto queda para revisar a mano."
                  onClick={async () => {
                    if (await dlg.confirmar(`Se clasificarán ${conSugerenciaAlta} documento(s) con la clasificación aprendida de su proveedor.`,
                      { detalle: 'Sólo se aplican las sugerencias de confianza ALTA; las demás quedan sin tocar para que las revises.' })) aplicarSugerencias.mutate();
                  }}>
                  <Sparkles className="w-4 h-4" /> Aplicar sugerencias ({conSugerenciaAlta})
                </Button>
              )}
              {aprendeDelProveedor && (
                <Button size="sm" variant="outline" isLoading={descartar.isPending}
                  title="Para los comprobantes que revisaste y NO corresponde registrar. Salen de pendientes y quedan con su motivo — se puede deshacer."
                  onClick={async () => {
                    const v = await dlg.pedir(`Descartar ${seleccion.size} comprobante(s)`,
                      [{ key: 'motivo', label: '¿Por qué no corresponde registrarlos?', minimo: 5 }],
                      { detalle: 'No se borran: salen de «pendientes» y quedan con tu nombre y el motivo.' });
                    if (v) descartar.mutate(v.motivo.trim());
                  }}>
                  <Archive className="w-4 h-4" /> Descartar ({seleccion.size})
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setSeleccion(new Set())}>
                Limpiar selección
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {heredanN > 0 && <><span className="font-medium text-foreground">{heredanN}</span> de la selección {heredanN === 1 ? 'hereda' : 'heredan'} del documento de sustento (NC/ND/retención): categoría, proyecto y centro se toman del sustento automáticamente. </>}
              {requierenN > 0
                ? <><span className="font-medium text-foreground">{requierenN}</span> {requierenN === 1 ? 'requiere' : 'requieren'} la categoría y el centro de costo de arriba (se aplican como global; el proyecto es opcional).</>
                : <>No necesitas elegir categoría ni centro para esta selección.</>}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Listado */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">
              Documentos ({documentos.length}{documentos.length === 500 ? '+ — afine los filtros' : ''})
            </CardTitle>
            <ExportarBotones
              nombre={nombreArchivo} titulo={cfg.titulo} headers={HEADERS_EXP} filas={filasExport}
              colsDerecha={[0, 6]} subtitulo={`${new Date().toLocaleString(LOCALE)} · ${documentosOrdenados.length} documento(s)`}
              fmtPDF={(v, c) => (c === 6 ? usd(Number(v)) : String(v ?? ''))}
              disabled={documentosOrdenados.length === 0} />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right w-12">N°</th>
                <th className="py-2 pr-2 w-8">
                  <input type="checkbox" className="h-4 w-4 rounded border-input align-middle"
                    checked={todosSeleccionados}
                    onChange={toggleTodos}
                    title="Seleccionar todos los visibles" />
                </th>
                <ThOrden campo="fecha" actual={orden} onSort={ordenarPor}>Fecha</ThOrden>
                <ThOrden campo="tipo" actual={orden} onSort={ordenarPor}>Tipo · Serie</ThOrden>
                <ThOrden campo="contraparte" actual={orden} onSort={ordenarPor}>{cfg.contraparte}</ThOrden>
                <ThOrden campo="total" actual={orden} onSort={ordenarPor} alinear="right">Total</ThOrden>
                {aprendeDelProveedor && (
                  <th className="py-2 pr-3" title="Cómo se clasificó antes a este proveedor">Sugerencia</th>
                )}
                <ThOrden campo="estado" actual={orden} onSort={ordenarPor}>Estado</ThOrden>
                <th className="py-2 pr-3 text-center">PDF</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {documentosOrdenados.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">
                  Sin documentos para los filtros seleccionados
                </td></tr>
              )}
              {documentosOrdenados.map((d, idx) => (
                <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">{idx + 1}</td>
                  <td className="py-2 pr-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-input align-middle"
                      checked={seleccion.has(d.id)}
                      disabled={!d.xml_parseado}
                      onChange={() => toggleSeleccion(d.id)} />
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">{d.fecha_emision || '—'}</td>
                  <td className="py-2 pr-3">
                    <span className="font-medium">{d.tipo_comprobante}</span>
                    <span className="text-muted-foreground"> · {d.serie_comprobante || '—'}</span>
                    {d.origen_erp && (
                      <span className="ml-2 rounded-full px-2 py-0.5 text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                        title="Emitido desde el ERP: proyecto y centro ya asignados">ERP</span>
                    )}
                    {heredaTipo(d.tipo_comprobante) && (
                      <span className="ml-2 rounded-full px-2 py-0.5 text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                        title="Hereda categoría, proyecto y centro del documento de sustento">hereda</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <p className="truncate max-w-72" title={String(d[cfg.campoRazon] || '')}>
                      {String(d[cfg.campoRazon] || '—')}
                    </p>
                    <p className="text-xs text-muted-foreground">{String(d[cfg.campoRuc] || '—')}</p>
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(d.importe_total)}</td>
                  {aprendeDelProveedor && (
                    <td className="py-2 pr-3">
                      {(() => {
                        const sug = sugPorDoc.get(d.id)?.sugerencia?.dimensiones?.categoria;
                        if (d.procesado || !sug?.valor) return <span className="text-xs text-muted-foreground">—</span>;
                        const cls = sug.nivel === 'alta' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          : sug.nivel === 'media' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'bg-muted text-muted-foreground';
                        return (
                          <span className={`rounded-full px-2 py-0.5 text-xs whitespace-nowrap ${cls}`}
                            title={`Así se clasificó a este proveedor ${sug.veces} de sus últimas ${sug.de} compras (confianza ${sug.nivel})`}>
                            {sug.valor} · {sug.veces}/{sug.de}
                          </span>);
                      })()}
                    </td>
                  )}
                  <td className="py-2 pr-3">
                    {!d.xml_parseado ? (
                      <span className="rounded-full px-2 py-0.5 text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                        title={d.parse_error || 'Sin detalle XML'}>Sin detalle</span>
                    ) : d.procesado ? (
                      <span className="rounded-full px-2 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        Procesado{d.categoria ? ` · ${d.categoria}` : ''}
                      </span>
                    ) : (
                      <span className="rounded-full px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                        Pendiente
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-center">
                    {urlRide(d.ride_url) ? (
                      <a href={urlRide(d.ride_url)!} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-primary hover:bg-accent"
                        title="Ver RIDE (PDF)">
                        <FileText className="w-4 h-4" />
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground" title="RIDE no disponible">—</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant={d.procesado ? 'outline' : 'default'}
                      disabled={!d.xml_parseado} onClick={() => setDocId(d.id)}>
                      {d.procesado ? 'Editar' : 'Procesar'}
                    </Button>
                  </td>
                </tr>
              ))}
              {documentosOrdenados.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3 text-right" colSpan={5}>Totales ({documentosOrdenados.length})</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(documentosOrdenados.reduce((a, d) => a + Number(d.importe_total || 0), 0))}</td>
                  <td colSpan={3}></td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {docId && (
        <PanelProcesar
          cfg={cfg}
          docId={docId}
          categorias={categorias}
          partners={partners}
          onClose={() => setDocId(null)}
          onSaved={() => {
            setDocId(null);
            void queryClient.invalidateQueries({ queryKey: ['sriProcesarListar'] });
            void queryClient.invalidateQueries({ queryKey: ['sriPartners'] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 981 · Prefactura del proveedor: enlazar / desenlazar con la factura en proceso
// ---------------------------------------------------------------------------
interface PfCand { id: string; referencia: string | null; archivo: string | null; total: number; lineas: number; diferencia: number; fecha_desde?: string | null; fecha_hasta?: string | null }
function PrefacturaEnlace({ documentoId }: { documentoId: string }) {
  const qc = useQueryClient();
  const [viendo, setViendo] = useState<string | null>(null);
  const { data } = useQuery<{ aplica: boolean; enlazada: PfCand | null; candidatas: PfCand[] }>({
    queryKey: ['fin-pf-doc', documentoId],
    queryFn: async () => (await apiClient.post('/rpc/fin_prefactura_para_documento', { _documento_id: documentoId }, FIN)).data,
  });
  const refrescar = () => { void qc.invalidateQueries({ queryKey: ['fin-pf-doc', documentoId] }); void qc.invalidateQueries({ queryKey: ['fin-prefacturas'] }); };
  const enlazar = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/fin_prefactura_enlazar', { _prefactura: id, _documento: documentoId }, FIN)).data,
    onSuccess: (r: { diferencia: number }) => { toast.success(Math.abs(Number(r.diferencia)) <= 0.01 ? 'Prefactura enlazada: los totales cuadran' : `Prefactura enlazada (diferencia ${usd(Number(r.diferencia))})`); refrescar(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo enlazar'),
  });
  const desenlazar = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/fin_prefactura_desenlazar', { _prefactura: id }, FIN)).data,
    onSuccess: () => { toast.success('Prefactura desenlazada'); refrescar(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo desenlazar'),
  });
  if (!data?.aplica) return null;
  const dif = (d: number) => (Math.abs(Number(d)) <= 0.01
    ? <span className="text-emerald-700 dark:text-emerald-400">✓ cuadra con la factura</span>
    : <span className="text-amber-700 dark:text-amber-400">diferencia {usd(Number(d))} (factura − prefactura)</span>);
  return (
    <div className="space-y-2 rounded-lg border p-3 text-sm">
      <p className="flex items-center gap-1.5 font-semibold"><FileSpreadsheet className="h-4 w-4" /> Prefactura del proveedor</p>
      {data.enlazada ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">Enlazada</span>
            <span>{data.enlazada.referencia ?? data.enlazada.archivo ?? 'Prefactura'} · {data.enlazada.lineas} línea(s) · {usd(data.enlazada.total)}</span>
            {dif(data.enlazada.diferencia)}
            <Button size="sm" variant="ghost" onClick={() => setViendo(viendo === data.enlazada!.id ? null : data.enlazada!.id)}>{viendo === data.enlazada.id ? 'Ocultar detalle' : 'Ver detalle'}</Button>
            <Button size="sm" variant="ghost" className="text-red-600" isLoading={desenlazar.isPending} onClick={() => desenlazar.mutate(data.enlazada!.id)}>Desenlazar</Button>
          </div>
          {viendo === data.enlazada.id && <DetallePrefactura id={data.enlazada.id} />}
        </>
      ) : data.candidatas.length === 0 ? (
        <p className="text-xs text-muted-foreground">No hay prefacturas pendientes de este proveedor. Puede cargarla en Compras y Pagos → Prefacturas de proveedores.</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">Prefacturas pendientes de este proveedor (la más cercana al total de la factura primero). Enlace la que corresponde:</p>
          <ul className="space-y-1.5">
            {data.candidatas.map((c, i) => (
              <li key={c.id} className="space-y-1">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-medium">{c.referencia ?? c.archivo ?? 'Prefactura'}</span>
                  <span className="text-xs text-muted-foreground">{c.lineas} línea(s) · {usd(c.total)}</span>
                  {dif(c.diferencia)}
                  <Button size="sm" variant={i === 0 ? 'default' : 'outline'} isLoading={enlazar.isPending && enlazar.variables === c.id} onClick={() => enlazar.mutate(c.id)}>Enlazar</Button>
                  <Button size="sm" variant="ghost" onClick={() => setViendo(viendo === c.id ? null : c.id)}>{viendo === c.id ? 'Ocultar' : 'Ver detalle'}</Button>
                </div>
                {viendo === c.id && <DetallePrefactura id={c.id} />}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel (overlay) de procesamiento de un documento
// ---------------------------------------------------------------------------
function PanelProcesar({
  cfg, docId, categorias, partners, onClose, onSaved,
}: {
  cfg: ModoConfig;
  docId: string;
  categorias: string[];
  partners: Partner[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data, isLoading } = useQuery<Detalle>({
    queryKey: ['sriProcesarDetalle', cfg.rpcDetalle, docId],
    queryFn: async () => (await apiClient.post(`/rpc/${cfg.rpcDetalle}`, { _documento_id: docId }, FIN)).data,
  });

  const { modo: modoCentro } = useCentroCostoModo();
  const { data: centrosContables = [] } = useCentrosContables(modoCentro === 'contable');
  const labelCentro = labelCentroCosto(modoCentro);

  const [categoria, setCategoria] = useState('');
  const [proyectoId, setProyectoId] = useState('');   // '' = sin proyecto
  const [centroId, setCentroId] = useState('');        // centro de costo (obligatorio) — partner o contable según el modo
  const [observaciones, setObservaciones] = useState('');
  const centros = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('centro_costo')), [partners]);
  const [porLinea, setPorLinea] = useState<Record<number, AsignacionLinea>>({});

  useEffect(() => {
    if (!data) return;
    const t = data.ticket;
    const erp = data.erp_origen;
    setCategoria(t?.categoria ?? erp?.categoria ?? '');
    setProyectoId(t?.proyecto_id ?? erp?.proyecto_id ?? '');
    setCentroId(modoCentro === 'contable'
      ? (t?.cc_contable_id ?? erp?.cc_contable_id ?? '')
      : (t?.centro_costo_id ?? erp?.centro_costo_id ?? ''));
    setObservaciones(t?.observaciones || '');
    const iniciales: Record<number, AsignacionLinea> = {};
    for (const l of data.lineas) {
      iniciales[l.numero_linea] = {
        categoria: l.categoria && l.categoria !== t?.categoria ? l.categoria : '',
        proyecto_id: l.proyecto_id && l.proyecto_id !== t?.proyecto_id ? l.proyecto_id : '',
      };
    }
    setPorLinea(iniciales);
  }, [data, modoCentro]);

  const grabar = useMutation({
    mutationFn: async () => {
      const lineas = Object.entries(porLinea)
        .filter(([, v]) => v.categoria || v.proyecto_id)
        .map(([n, v]) => ({
          numero_linea: Number(n),
          ...(v.categoria ? { categoria: v.categoria } : {}),
          ...(v.proyecto_id ? { proyecto_id: v.proyecto_id } : {}),
        }));
      const { data: res } = await apiClient.post(`/rpc/${cfg.rpcGrabar}`, {
        _documento_id: docId,
        _categoria: categoria,
        _proyecto_id: proyectoId || null,
        _centro_costo_id: modoCentro === 'contable' ? null : (centroId || null),
        _observaciones: observaciones || null,
        _lineas: lineas,
        _cc_contable_id: modoCentro === 'contable' ? (centroId || null) : null,
      }, FIN);
      return res;
    },
    onSuccess: (res: any) => {
      toast.success(res?.reconciliado ? res.message : `Documento procesado (${res?.lineas ?? 0} líneas)`);
      onSaved();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'No se pudo grabar el documento');
    },
  });

  const doc = data?.documento;
  // Emitido de origen ERP: proyecto/centro ya vienen de la factura (solo lectura); solo falta categoría.
  const esErp = !!data?.erp_origen || ['facturacion', 'facturacion_especializada'].includes(data?.ticket?.origen || '');
  // NC/ND/Retención: hereda todo del documento de sustento. Si el sustento no existe
  // o no fue clasificado, no se puede procesar (bloqueado).
  const sustento = data?.sustento ?? null;
  const heredaSustento = !!sustento && !esErp;
  const sustentoListo = heredaSustento && !!sustento && sustento.encontrado && sustento.procesado;
  const sustentoBloqueado = heredaSustento && !sustentoListo;
  const soloLectura = esErp || heredaSustento;   // proyecto/centro/líneas no editables
  const tipoSustentoLabel = sustento?.tipo === 'retencion' ? 'documento sustentado' : 'documento modificado';
  const partnerOptions = useMemo(() => partners.map((p) => (
    <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion})</option>
  )), [partners]);

  const setLinea = (n: number, campo: keyof AsignacionLinea, valor: string) =>
    setPorLinea((prev) => ({ ...prev, [n]: { ...(prev[n] || { categoria: '', proyecto_id: '' }), [campo]: valor } }));

  return (
    <PanelIntegrado title={doc ? `${doc.tipo_comprobante} ${doc.serie_comprobante || ''}` : 'Cargando…'} onClose={onClose} wide>
      <div className="space-y-5">
          {doc && (
            <p className="text-sm text-muted-foreground -mt-2">
              {String(doc[cfg.campoRazon] || '(sin identificar)')} · {cfg.contraparte} {String(doc[cfg.campoRuc] || '—')} · {doc.fecha_emision}
              <span className="ml-2">Subtotal {usd(doc.valor_sin_impuestos)} · IVA {usd(doc.iva)} ·
                <span className="font-semibold text-foreground"> Total {usd(doc.importe_total)}</span></span>
            </p>
          )}
          {isLoading && <div className="py-10 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-primary" /></div>}

          {/* 981: enlace con la prefactura (Excel) del proveedor — solo recibidos */}
          {data && doc && cfg.rpcGrabar === 'sri_procesar_grabar' && /^factura/i.test(String(doc.tipo_comprobante || '')) && (
            <PrefacturaEnlace documentoId={docId} />
          )}

          {data && doc && (
            <>
              {/* Cabecera: clasificación heredada del sustento (NC/ND/Retención) o asignación global manual */}
              {heredaSustento && sustento ? (
                sustentoBloqueado ? (
                  <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4 space-y-1">
                    <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                      No se puede procesar todavía
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      {!sustento.encontrado ? (
                        <>El {tipoSustentoLabel} <span className="font-medium">{sustento.num_doc || 'sin referencia'}</span> no
                          existe en el sistema. Descárguelo/regístrelo y clasifíquelo antes de procesar este comprobante.</>
                      ) : (
                        <>El {tipoSustentoLabel} <span className="font-medium">{sustento.num_doc || 'sin referencia'}</span> aún
                          no ha sido clasificado. Procéselo primero para que este comprobante herede su categoría, proyecto
                          y centro de costo.</>
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
                    <p className="text-sm font-semibold flex items-center gap-2">
                      Clasificación heredada del {tipoSustentoLabel}
                      <span className="rounded-full px-2 py-0.5 text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                        {sustento.num_doc || 'sustento'}
                      </span>
                    </p>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <p><span className="text-muted-foreground">Categoría: </span>
                        <span className="font-medium">{sustento.categoria || '—'}</span></p>
                      <p><span className="text-muted-foreground">Centro de costo: </span>
                        <span className="font-medium">{sustento.centro_costo_nombre || '—'}</span></p>
                      <p><span className="text-muted-foreground">Proyecto: </span>
                        <span className="font-medium">{sustento.proyecto_nombre || PROYECTO_VACIO}</span></p>
                      {sustento.observaciones && (
                        <p><span className="text-muted-foreground">Observaciones: </span>
                          <span className="font-medium">{sustento.observaciones}</span></p>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Los comprobantes de {sustento.tipo === 'retencion' ? 'retención' : 'nota de crédito/débito'} no
                      se clasifican por separado: heredan la categoría, el proyecto y el centro de costo del {tipoSustentoLabel}.
                    </p>
                  </div>
                )
              ) : (
                <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
                <p className="text-sm font-semibold flex items-center gap-2">
                  Asignación global del documento
                  {esErp && (
                    <span className="rounded-full px-2 py-0.5 text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      Emitido desde el ERP
                    </span>
                  )}
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium" htmlFor="pp-cat">Categoría *</label>
                    <select id="pp-cat" className={selectCls} value={categoria}
                      onChange={(e) => setCategoria(e.target.value)}>
                      <option value="">— Seleccione —</option>
                      {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium" htmlFor="pp-proyecto">Proyecto{esErp && ' (de la factura)'}</label>
                    <select id="pp-proyecto" className={`${selectCls} max-w-72 ${esErp ? 'opacity-70' : ''}`} value={proyectoId}
                      disabled={esErp} onChange={(e) => setProyectoId(e.target.value)}>
                      <option value="">{PROYECTO_VACIO}</option>
                      {partnerOptions}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium" htmlFor="pp-centro">{labelCentro}{esErp ? ' (de la factura)' : ' *'}</label>
                    <select id="pp-centro" className={`${selectCls} max-w-72 ${esErp ? 'opacity-70' : ''}`} value={centroId}
                      disabled={esErp} onChange={(e) => setCentroId(e.target.value)}>
                      <option value="">— Seleccione —</option>
                      {modoCentro === 'contable'
                        ? centrosContables.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)
                        : centros.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 min-w-48 space-y-1">
                    <label className="text-xs font-medium" htmlFor="pp-obs">Observaciones</label>
                    <input id="pp-obs" className={`${inputCls} w-full`} value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)} placeholder="Opcional" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {esErp
                    ? <>Esta factura se emitió desde el ERP: <span className="font-medium text-foreground">proyecto y centro ya vienen de la factura</span> (no se modifican aquí). Solo definí la categoría.</>
                    : <>{cfg.contraparte} del documento: <span className="font-medium text-foreground">
                        {data.ticket?.partner_nombre || String(doc[cfg.campoRazon] || '(se asigna al grabar)')}</span>
                        {' '}(automático). Las líneas sin asignación propia heredan la categoría y el proyecto globales.</>}
                </p>
              </div>
              )}

              {/* Líneas */}
              <div>
                <p className="text-sm font-semibold mb-2">Rubros del documento ({data.lineas.length})</p>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                        <th className="py-2 px-2">#</th>
                        <th className="py-2 px-2">Descripción</th>
                        <th className="py-2 px-2 text-right">Cant.</th>
                        <th className="py-2 px-2 text-right">P. Unit.</th>
                        <th className="py-2 px-2 text-right">Subtotal</th>
                        <th className="py-2 px-2 text-right">IVA</th>
                        <th className="py-2 px-2">Categoría (línea)</th>
                        <th className="py-2 px-2">Proyecto (línea)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.lineas.map((l) => (
                        <tr key={l.numero_linea} className="border-b last:border-0 align-top">
                          <td className="py-2 px-2">{l.numero_linea}</td>
                          <td className="py-2 px-2 max-w-80">
                            <p className="truncate" title={l.descripcion}>{l.descripcion}</p>
                            {l.codigo_principal && <p className="text-xs text-muted-foreground">cód. {l.codigo_principal}</p>}
                          </td>
                          <td className="py-2 px-2 text-right">{l.cantidad ?? '—'}</td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">{usd(l.precio_unitario)}</td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">{usd(l.precio_total_sin_impuesto)}</td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">
                            {l.iva_valor != null ? usd(l.iva_valor) : '—'}
                            {l.iva_tarifa != null && <span className="text-xs text-muted-foreground"> ({l.iva_tarifa}%)</span>}
                          </td>
                          <td className="py-2 px-2">
                            <select className={`${selectCls} w-44`} value={porLinea[l.numero_linea]?.categoria || ''}
                              disabled={soloLectura}
                              onChange={(e) => setLinea(l.numero_linea, 'categoria', e.target.value)}>
                              <option value="">{heredaSustento ? '(hereda del documento)' : '(hereda global)'}</option>
                              {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </td>
                          <td className="py-2 px-2">
                            <select className={`${selectCls} w-48`} value={porLinea[l.numero_linea]?.proyecto_id || ''}
                              disabled={soloLectura}
                              onChange={(e) => setLinea(l.numero_linea, 'proyecto_id', e.target.value)}>
                              <option value="">{heredaSustento ? '(hereda del documento)' : '(hereda global)'}</option>
                              {partnerOptions}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Impuestos y pagos (solo lectura) */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-md border p-3">
                  <p className="text-sm font-semibold mb-2">Impuestos</p>
                  {data.impuestos.length === 0
                    ? <p className="text-xs text-muted-foreground">Sin impuestos</p>
                    : data.impuestos.map((t) => (
                      <p key={t.id} className="text-sm flex justify-between">
                        <span className="text-muted-foreground">
                          Cód. {t.codigo}/{t.codigo_porcentaje} · base {usd(t.base_imponible)}
                        </span>
                        <span className="font-medium">{usd(t.valor)}</span>
                      </p>
                    ))}
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-sm font-semibold mb-2">Formas de pago</p>
                  {data.pagos.length === 0
                    ? <p className="text-xs text-muted-foreground">Sin formas de pago (según el tipo de comprobante)</p>
                    : data.pagos.map((p) => (
                      <p key={p.id} className="text-sm flex justify-between">
                        <span className="text-muted-foreground">{p.descripcion}
                          {p.plazo ? ` · ${p.plazo} ${p.unidad_tiempo || 'días'}` : ''}</span>
                        <span className="font-medium">{usd(p.total)}</span>
                      </p>
                    ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={onClose}>Cancelar</Button>
                <Button disabled={sustentoBloqueado} onClick={() => {
                  if (!heredaSustento) {
                    if (!categoria) { toast.error('Seleccione la categoría'); return; }
                    if (!esErp && !centroId) { toast.error('Seleccione el centro de costo'); return; }
                  }
                  grabar.mutate();
                }} isLoading={grabar.isPending}>
                  {!grabar.isPending && (data.ticket ? <CheckCircle2 className="w-4 h-4" /> : <Save className="w-4 h-4" />)}
                  {data.ticket ? 'Actualizar' : 'Grabar'}
                </Button>
              </div>
            </>
          )}
      </div>
    </PanelIntegrado>
  );
}
